import React, { useState, useContext } from 'react';
import { ArrowDown2, Trash, Notification, Global, Microphone2, Profile, People, Lock, Setting2, MessageQuestion, MessageText, NoteText } from 'iconsax-react';
import SettingsMenuItem from '../components/SettingsMenuItem';
import SettingsToggleRow from '../components/SettingsToggleRow';
import SelectField from '../components/SelectField';
import { ThemeContext } from '../theme';


const LANGUAGE_OPTIONS = [
  'English', 'Spanish', 'French', 'German', 'Portuguese',
  'Italian', 'Dutch', 'Japanese', 'Korean', 'Mandarin Chinese',
  'Arabic', 'Hindi', 'Turkish', 'Polish', 'Swedish',
];

const SETTINGS_MENU = [
  { id: 'notifications', label: 'Notifications',   icon: Notification    },
  { id: 'language',      label: 'Language',         icon: Global          },
  { id: 'narration',     label: 'Narration Voice',  icon: Microphone2     },
  { id: 'account',       label: 'Account',          icon: Profile         },
  { id: 'kidsProfile',   label: "Kid's Profile",    icon: People          },
  { id: 'privacy',       label: 'Privacy',          icon: Lock            },
  { id: 'permissions',   label: 'Permissions',      icon: Setting2        },
  { id: 'faqs',          label: "FAQ's",            icon: MessageQuestion },
  { id: 'feedback',      label: 'Feedback',         icon: MessageText     },
  { id: 'terms',         label: 'Terms of Use',     icon: NoteText        },
];

// Tabs that use the toggle-row panel layout
const TOGGLE_PANELS = {
  notifications: {
    title: 'Notification Settings',
    rows: [
      { key: 'n_screenTime',    label: 'Weekly screen time updates',           defaultOn: true  },
      { key: 'n_trending',      label: 'Trending stories suggestions',          defaultOn: false },
      { key: 'n_parentPosts',   label: 'Trending parent post suggestions',      defaultOn: true  },
      { key: 'n_activity',      label: 'Activity updates on your posts',        defaultOn: false },
      { key: 'n_festive',       label: 'Upcoming festive stories',              defaultOn: true  },
    ],
  },
  kidsProfile: {
    title: 'Kids Profile Settings',
    rows: [
      { key: 'kp_screenBreak',  label: 'Screen-time break reminders',    defaultOn: true },
      { key: 'kp_screenLimit',  label: 'Age specific screen-time limit',  defaultOn: true },
      { key: 'kp_nightMode',    label: 'Night mode',                      defaultOn: true },
    ],
  },
  permissions: {
    title: 'Permissions Settings',
    rows: [
      { key: 'pm_microphone',  label: 'Microphone',                      defaultOn: true  },
      { key: 'pm_camera',      label: 'Camera',                          defaultOn: false },
      { key: 'pm_location',    label: 'Location',                        defaultOn: false },
      { key: 'pm_history',     label: 'Save History',                    defaultOn: false },
      { key: 'pm_storage',     label: 'Storage access',                  defaultOn: true  },
      { key: 'pm_shareStories',label: 'Share stories for improvement',   defaultOn: false },
    ],
  },
};

const buildInitialToggles = () => {
  const state = {};
  Object.values(TOGGLE_PANELS).forEach(({ rows }) => {
    rows.forEach(({ key, defaultOn }) => { state[key] = defaultOn; });
  });
  return state;
};

// Language settings panel — two dropdowns per row
function LanguagePanel({ langs, onLangChange }) {
  const theme = useContext(ThemeContext);
  const SCALE = 0.70;
  return (
    <>
      <span
        className="font-satoshi"
        style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}
      >
        Language Settings
      </span>
      <div
        style={{
          backgroundColor: theme.cardBg,
          borderRadius: 16,
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flexShrink: 0,
        }}
      >
        {/* Row 1: System Language + Story Language */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectField
              label="System Language"
              options={LANGUAGE_OPTIONS}
              value={langs.system}
              onChange={(v) => onLangChange('system', v)}
              scale={SCALE}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectField
              label="Story Language (Default)"
              options={LANGUAGE_OPTIONS}
              value={langs.story}
              onChange={(v) => onLangChange('story', v)}
              scale={SCALE}
            />
          </div>
        </div>
        {/* Row 2: AI Narration Language (half width) */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectField
              label="AI Narration Language (Default)"
              options={LANGUAGE_OPTIONS}
              value={langs.narration}
              onChange={(v) => onLangChange('narration', v)}
              scale={SCALE}
            />
          </div>
          {/* Empty spacer to keep half-width layout */}
          <div style={{ flex: 1, minWidth: 0 }} />
        </div>
      </div>
    </>
  );
}

const ACCOUNT_ITEMS = [
  'Manage Profiles',
  'Update e-mail id',
  'Update password',
  'Temporarily deactivate account',
  'Delete account',
];

function AccountPanel() {
  const theme = useContext(ThemeContext);
  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke="#b3c4cb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}>
        Account Settings
      </span>
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        {ACCOUNT_ITEMS.map((item, i) => (
          <div
            key={item}
            role="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 16px',
              borderBottom: i === ACCOUNT_ITEMS.length - 1 ? 'none' : `1px solid ${theme.border}`,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <span
              className="font-satoshi"
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 500,
                color: theme.text100,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item}
            </span>
            <ChevronRight />
          </div>
        ))}
      </div>
    </>
  );
}

const PRIVACY_ITEMS = [
  'Information we collect',
  'How we use information',
  'How we share information',
  'Third party services',
  'Security',
  "Children's privacy",
  'Changes to this privacy policy',
];

function PrivacyPanel() {
  const theme = useContext(ThemeContext);
  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke="#b3c4cb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}>
        Privacy Settings
      </span>
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        {PRIVACY_ITEMS.map((item, i) => (
          <div
            key={item}
            role="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 16px',
              borderBottom: i === PRIVACY_ITEMS.length - 1 ? 'none' : `1px solid ${theme.border}`,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <span
              className="font-satoshi"
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 500,
                color: theme.text100,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item}
            </span>
            <ChevronRight />
          </div>
        ))}
      </div>
    </>
  );
}

const AI_VOICES = ['Martha - Female', 'Steve - Male', 'Emily - Female', 'Jacob - Male'];

function NarrationVoicePanel() {
  const theme = useContext(ThemeContext);
  const [aiExpanded, setAiExpanded] = useState(true);
  const [customExpanded, setCustomExpanded] = useState(true);
  const [customVoices, setCustomVoices] = useState(['Cecillia', 'Arthy', 'Khushi']);

  const SectionHeader = ({ label, expanded, onToggle }) => (
    <div
      role="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderBottom: `1px solid ${theme.border}`,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span className="font-satoshi" style={{ flex: 1, fontSize: 13, fontWeight: 600, color: theme.text100, lineHeight: '18px' }}>
        {label}
      </span>
      <span style={{ display: 'flex', transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
        <ArrowDown2 size={16} variant="Linear" color={theme.text400} />
      </span>
    </div>
  );

  const VoiceRow = ({ label, onDelete, isLast }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px 10px 28px',
        borderBottom: isLast ? 'none' : `1px solid ${theme.border}`,
        gap: 10,
      }}
    >
      <span className="font-satoshi" style={{ flex: 1, fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {onDelete && (
        <button
          onClick={onDelete}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Trash size={16} variant="Linear" color={theme.text400} />
        </button>
      )}
    </div>
  );

  return (
    <>
      <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}>
        Narration Voice Settings
      </span>

      {/* AI Voices */}
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        <SectionHeader label="AI Voices" expanded={aiExpanded} onToggle={() => setAiExpanded(v => !v)} />
        {aiExpanded && AI_VOICES.map((v, i) => (
          <VoiceRow key={v} label={v} isLast={i === AI_VOICES.length - 1} />
        ))}
      </div>

      {/* Custom Voices */}
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        <SectionHeader label="Custom Voices" expanded={customExpanded} onToggle={() => setCustomExpanded(v => !v)} />
        {customExpanded && customVoices.map((v, i) => (
          <VoiceRow
            key={v}
            label={v}
            isLast={i === customVoices.length - 1}
            onDelete={() => setCustomVoices(prev => prev.filter(name => name !== v))}
          />
        ))}
      </div>

      {/* Add Custom Voice */}
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 16px',
          backgroundColor: theme.cardBg,
          border: `1.5px solid ${theme.border}`,
          borderRadius: 16,
          cursor: 'pointer',
          width: '100%',
          flexShrink: 0,
        }}
        onClick={() => {
          const name = prompt('Enter custom voice name:');
          if (name?.trim()) setCustomVoices(prev => [...prev, name.trim()]);
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke={theme.text200} strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 700, color: theme.text200, lineHeight: '18px' }}>
          Add Custom Voice
        </span>
      </button>
    </>
  );
}

const FAQ_ITEMS = [
  {
    q: 'What is Taleo?',
    a: 'Taleo is an AI-powered storytelling app designed for children. It creates personalised, age-appropriate stories using your inputs to spark imagination and a love for reading.',
  },
  {
    q: 'How can I provide inputs to the app to get the best output',
    a: 'You can provide character names, themes, settings, and moral values when creating a story. The more specific your inputs, the more personalised and engaging the generated story will be.',
  },
  {
    q: 'How does Taleo work?',
    a: 'Taleo uses advanced AI to generate unique stories based on the preferences you set. Simply choose a theme, characters, and settings, and Taleo crafts a custom story with optional narration.',
  },
  {
    q: 'Is Taleo suitable for kids of all age group?',
    a: 'Yes! Taleo adapts its content and vocabulary to the age group you set in your kid\'s profile, ensuring stories are always age-appropriate and engaging for your child.',
  },
  {
    q: 'Can Taleo be used for educational purposes?',
    a: 'Absolutely. Taleo can weave educational themes, moral lessons, and learning concepts into stories, making it a great tool for parents and educators alike.',
  },
  {
    q: 'How many kids profile can I have in one account?',
    a: 'You can create multiple kids profiles under a single Taleo account, each with its own age settings, reading history, and preferences.',
  },
  {
    q: 'Can I share the generated stories on social media platforms?',
    a: 'Yes, you can share your favourite generated stories directly to social media platforms or save them to read later within the app.',
  },
  {
    q: 'How does Taleo protects my privacy?',
    a: 'Taleo takes privacy seriously. We collect only the data necessary to deliver the service, never sell your data, and give you full control over what is shared. See our Privacy policy for full details.',
  },
  {
    q: 'What if I forgot my account password?',
    a: 'You can reset your password by tapping "Forgot password" on the login screen. A reset link will be sent to your registered email address.',
  },
];

function FaqsPanel() {
  const theme = useContext(ThemeContext);
  const [openIndex, setOpenIndex] = useState(null);

  const ChevronDown = ({ rotated }) => (
    <span
      style={{
        display: 'flex',
        flexShrink: 0,
        transition: 'transform 0.2s',
        transform: rotated ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M6 9l6 6 6-6" stroke={theme.text400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  return (
    <>
      <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}>
        FAQs
      </span>
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = openIndex === i;
          const isLast = i === FAQ_ITEMS.length - 1;
          return (
            <div key={i} style={{ borderBottom: isLast ? 'none' : `1px solid ${theme.border}` }}>
              <div
                role="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span
                  className="font-satoshi"
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 500,
                    color: theme.text100,
                    lineHeight: '18px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.q}
                </span>
                <ChevronDown rotated={isOpen} />
              </div>
              {isOpen && (
                <div style={{ padding: '0 16px 14px 16px' }}>
                  <span
                    className="font-satoshi"
                    style={{ fontSize: 12, fontWeight: 400, color: theme.text400, lineHeight: '18px' }}
                  >
                    {item.a}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

const FEEDBACK_ITEMS = [
  'Tell us what & how can we improve?',
  'Report a problem',
  'Rate us on Appstore',
];

function FeedbackPanel() {
  const theme = useContext(ThemeContext);
  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke="#b3c4cb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      <span className="font-satoshi" style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}>
        Feedback
      </span>
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 16, overflow: 'hidden', flexShrink: 0 }}>
        {FEEDBACK_ITEMS.map((item, i) => (
          <div
            key={item}
            role="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 16px',
              borderBottom: i === FEEDBACK_ITEMS.length - 1 ? 'none' : `1px solid ${theme.border}`,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <span
              className="font-satoshi"
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 500,
                color: theme.text100,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item}
            </span>
            <ChevronRight />
          </div>
        ))}
      </div>
    </>
  );
}

// Placeholder panel for tabs without detailed settings yet
function PlaceholderPanel({ title }) {
  const theme = useContext(ThemeContext);
  return (
    <div
      style={{
        backgroundColor: theme.cardBg,
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px',
        gap: 10,
        flex: 1,
        minHeight: 120,
      }}
    >
      <span
        className="font-satoshi"
        style={{ fontSize: 14, fontWeight: 600, color: theme.text100, lineHeight: '20px' }}
      >
        {title}
      </span>
      <span
        className="font-satoshi"
        style={{ fontSize: 13, color: '#b3c4cb', lineHeight: '18px', textAlign: 'center' }}
      >
        Settings for this section are coming soon.
      </span>
    </div>
  );
}

export default function SettingsScreen() {
  const theme = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('notifications');
  const [toggles, setToggles] = useState(buildInitialToggles);
  const [langs, setLangs] = useState({ system: 'English', story: 'English', narration: 'English' });

  const handleToggle = (key) => (value) => {
    setToggles((prev) => ({ ...prev, [key]: value }));
  };

  const handleLangChange = (key, value) => {
    setLangs((prev) => ({ ...prev, [key]: value }));
  };

  const togglePanel = TOGGLE_PANELS[activeTab];

  const renderPanel = () => {
    if (activeTab === 'language') {
      return <LanguagePanel langs={langs} onLangChange={handleLangChange} />;
    }
    if (activeTab === 'narration') {
      return <NarrationVoicePanel />;
    }
    if (activeTab === 'account') {
      return <AccountPanel />;
    }
    if (activeTab === 'privacy') {
      return <PrivacyPanel />;
    }
    if (activeTab === 'faqs') {
      return <FaqsPanel />;
    }
    if (activeTab === 'feedback') {
      return <FeedbackPanel />;
    }
    if (togglePanel) {
      return (
        <>
          <span
            className="font-satoshi"
            style={{ fontSize: 13, fontWeight: 500, color: theme.text400, lineHeight: '18px', flexShrink: 0 }}
          >
            {togglePanel.title}
          </span>
          <div
            style={{
              backgroundColor: theme.cardBg,
              borderRadius: 16,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            {togglePanel.rows.map((row, i) => (
              <SettingsToggleRow
                key={row.key}
                label={row.label}
                checked={toggles[row.key]}
                onChange={handleToggle(row.key)}
                isFirst={i === 0}
                isLast={i === togglePanel.rows.length - 1}
              />
            ))}
          </div>
        </>
      );
    }
    return <PlaceholderPanel title={SETTINGS_MENU.find((m) => m.id === activeTab)?.label ?? ''} />;
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        backgroundColor: theme.cardBg100,
        padding: 12,
        gap: 12,
        overflow: 'hidden',
      }}
    >
      {/* ── Left: Settings navigation card ── */}
      <div
        style={{
          width: 280,
          flexShrink: 0,
          backgroundColor: theme.cardBg,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'flex-start',
        }}
      >
        {SETTINGS_MENU.map((item, i) => (
          <SettingsMenuItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            active={activeTab === item.id}
            onClick={() => setActiveTab(item.id)}
            isFirst={i === 0}
            isLast={i === SETTINGS_MENU.length - 1}
          />
        ))}
      </div>

      {/* ── Right: Sub-settings detail panel ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          overflow: 'auto',
          minWidth: 0,
        }}
      >
        {renderPanel()}
      </div>
    </div>
  );
}
